import React from 'react';
import PropTypes from 'prop-types';
import { Input } from '@wds/input';
import { InputGroup, InputGroupAddon } from '@wds/input-group';
import {
	IconCaretLeft,
	IconCaretRight,
	IconSearch,
} from '@wds/icons';
import {
	GroupedMenu,
	GroupedMenuBody,
	GroupedMenuBodyLeft,
	GroupedMenuBodyRight,
	GroupedMenuHeader,
	GroupedMenuItem,
} from 'sma.common/components/ads/grouped-menu';

/**
 * This class follows old React syntax (no hooks) as it is derived from
 * a class in another file, and it was left mostly untouched to prevent
 * unintentionally changing behaviour
 */
export default class NestedCategoryMenu extends React.Component {
	// Check if pane has an element that has children
	static paneHasParent(data) {
		const itemsWithParents = data.map(item => Object.keys(item).includes('children'));

		return itemsWithParents.length > 0;
	}

	static search(searchTerm, treeNode) {
		const endResult = [];

		function childMatchesSearch(node, search) {
			let result = {
				...node,
				label: node.label,
				key: node.key,
				originalLabel: node.originalLabel,
				disabled: node.disabled,
			};

			let hasMatch = false;

			if (node.label.toLowerCase().includes(search)) {
				if (node.hasOwnProperty('children')) {
					result.children = node.children;
				}

				hasMatch = true;
			} else if (node.hasOwnProperty('children')) {
				const matchedChildren = [];

				node.children.forEach((subNode) => {
					const childMatch = childMatchesSearch(subNode, search);

					if (childMatch) {
						matchedChildren.push(childMatch);
					}
				});

				result.children = matchedChildren;
				hasMatch = matchedChildren.length > 0;
			}

			if (!hasMatch) {
				result = null;
			}

			return result;
		}

		treeNode.forEach((item) => {
			const searchResult = childMatchesSearch(item, searchTerm.toLowerCase());
			if (searchResult !== null) {
				endResult.push(searchResult);
			}
		});

		return endResult;
	}

	constructor(props) {
		super(props);

		const { categoryList } = props;

		this.state = {
			data: categoryList,
			leftPane: categoryList,
			rightPane: [],
			parent: null,
			previouslySelected: null,
			previousGroup: [],
			depth: 0,
			searchTerm: '',
		};

		// ES6 React.Component doesn't auto bind methods to itself
		this.handleSearch = this.handleSearch.bind(this);
		this.handleChildClick = this.handleChildClick.bind(this);
		this.handleParentClick = this.handleParentClick.bind(this);
		this.handlePreviousClick = this.handlePreviousClick.bind(this);
	}

	handleChildClick(e, item) {
		const { onChange } = this.props;
		onChange(item);
	}

	handlePreviousClick() {
		const {
			depth,
			leftPane,
			previousGroup,
			previouslySelected,
		} = this.state;

		this.setState({
			rightPane: leftPane,
			leftPane: previousGroup,
			depth: depth - 1,
			parent: previouslySelected,
		});
	}

	handleParentClick(e, item, panel) {
		const {
			depth,
			leftPane,
			parent,
			rightPane,
		} = this.state;

		if (!(parent && item.id === parent.id)) {
			let newState = this.state;
			if (panel === 'right' && NestedCategoryMenu.paneHasParent(rightPane)) {
				newState = {
					...newState,
					previousGroup: leftPane,
					leftPane: rightPane,
					depth: depth + 1,
				};
			}

			this.setState({
				...newState,
				previouslySelected: parent,
				parent: item,
				rightPane: item.children,
			});
		}
	}

	handleSearch(e) {
		const searchTerm = e.target.value;

		const {
			data,
		} = this.state;

		const resultTree = NestedCategoryMenu.search(searchTerm, data);

		let rightPane;

		if (!resultTree.length) {
			rightPane = [];
		} else {
			rightPane = resultTree[0].children || [];
		}

		this.setState({
			leftPane: resultTree,
			rightPane,
			searchTerm,
		});
	}

	paneRenderer(data, panel) {
		const {
			depth,
			parent,
			previouslySelected,
		} = this.state;

		const menuItems = [];
		if (depth > 0 && panel === 'left') {
			menuItems.push(
				<GroupedMenuItem
					onClick={e => this.handlePreviousClick(e, previouslySelected)}
					className="wds-flex previous"
				>
					<div>
						<IconCaretLeft />
					</div>
					<span>{previouslySelected.label}</span>
				</GroupedMenuItem>,
			);
		}

		menuItems.push(data.map((item) => {
			const hasChildren = 'children' in item;

			if (hasChildren) {
				const isSelected = (parent && item.id === parent.id);
				return (
					<GroupedMenuItem
						key={item.id}
						onClick={e => this.handleParentClick(e, item, panel)}
						className={`wds-flex wds-flex--between parent ${isSelected ? 'selected' : ''}`}
					>
						<span>{item.label}</span>
						<div className={`${isSelected ? '' : 'ads-invisible'}`}>
							<IconCaretRight />
						</div>
					</GroupedMenuItem>
				);
			}

			return (
				<GroupedMenuItem
					key={item.id}
					onClick={e => this.handleChildClick(e, item)}
				>
					{item.label}
				</GroupedMenuItem>
			);
		}));

		return menuItems;
	}

	render() {
		const {
			leftPane,
			rightPane,
			searchTerm,
		} = this.state;

		return (
			<GroupedMenu className="w-100">
				<GroupedMenuHeader>
					<InputGroup stretched>
						<Input
							onKeyDown={(e) => {
								if(e.key === 'Enter') {
									e.preventDefault();
								}
							}}
							value={searchTerm}
							onChange={this.handleSearch}
							placeholder={gettext('Search...')}
						/>
						<InputGroupAddon><IconSearch /></InputGroupAddon>
					</InputGroup>
				</GroupedMenuHeader>
				<GroupedMenuBody className="wds-flex">
					<GroupedMenuBodyLeft className="wds-flex__item--grow">
						{this.paneRenderer(leftPane, 'left')}
					</GroupedMenuBodyLeft>
					<GroupedMenuBodyRight className="wds-flex__item--grow">
						{this.paneRenderer(rightPane, 'right')}
					</GroupedMenuBodyRight>
				</GroupedMenuBody>
			</GroupedMenu>
		);
	}
}

export const categoryShape = PropTypes.shape({
	label: PropTypes.string.isRequired,
	id: PropTypes.string.isRequired,
	roles: PropTypes.arrayOf(PropTypes.string).isRequired,
	articleTag: PropTypes.string,
	formLink: PropTypes.bool.isRequired,
	relatedPrograms: PropTypes.shape({ required: PropTypes.bool.isRequired }),
	relatedStages: PropTypes.shape({ required: PropTypes.bool.isRequired }),
	relatedTasks: PropTypes.shape({ required: PropTypes.bool.isRequired }),
	relatedUsers: PropTypes.shape({ required: PropTypes.bool.isRequired }),
	relatedApplications: PropTypes.shape({ required: PropTypes.bool.isRequired }),
	relatedReports: PropTypes.shape({ required: PropTypes.bool.isRequired }),
	relatedAutomations: PropTypes.shape({ required: PropTypes.bool.isRequired }),
	adminCanRequestCall: PropTypes.bool,
});

export const categoryHeaderShape = PropTypes.shape({
	label: PropTypes.string.isRequired,
	id: PropTypes.string.isRequired,
	children: PropTypes.arrayOf(categoryShape),
	roles: PropTypes.arrayOf(PropTypes.string),
	articleTag: PropTypes.string,
	formLink: PropTypes.bool,
	relatedPrograms: PropTypes.shape({ required: PropTypes.bool.isRequired }),
	relatedStages: PropTypes.shape({ required: PropTypes.bool.isRequired }),
	relatedTasks: PropTypes.shape({ required: PropTypes.bool.isRequired }),
	relatedUsers: PropTypes.shape({ required: PropTypes.bool.isRequired }),
	relatedApplications: PropTypes.shape({ required: PropTypes.bool.isRequired }),
	relatedReports: PropTypes.shape({ required: PropTypes.bool.isRequired }),
	relatedAutomations: PropTypes.shape({ required: PropTypes.bool.isRequired }),
	adminCanRequestCall: PropTypes.bool,
});

NestedCategoryMenu.propTypes = {
	categoryList: PropTypes.arrayOf(categoryHeaderShape).isRequired,
	onChange: PropTypes.func.isRequired,
};

